import type { ReactNode } from "react";
import { HOME_ART } from "../assets";
import { Reveal } from "../Reveal";
import { SECTION_IDS, type MarketingCopy } from "../copy";
import { CloudOffIcon, DeviceIcon, VideoOffIcon } from "../icons";

export interface PrivacyProps {
  t: MarketingCopy;
}

const ICONS: ReactNode[] = [
  <DeviceIcon key="device" className="h-5 w-5" />,
  <VideoOffIcon key="video" className="h-5 w-5" />,
  <CloudOffIcon key="cloud" className="h-5 w-5" />,
];

/**
 * The camera promise, given a panel of its own.
 *
 * 🔴 THREE SENTENCES, AND EACH IS AN ARCHITECTURAL FACT, NOT A POLICY. Pose is
 * computed on the device (MediaPipe, self-hosted — ADR-025), no frame is
 * recorded (I5 — `check-no-exfil` fails the build on a `MediaRecorder`), and
 * nothing body-derived leaves the laptop (I1). A head teacher reading this is
 * reading what the code enforces, which is the only kind of privacy copy worth
 * printing on a children's product.
 *
 * ⚠️ IT IS THE PAGE'S ONE DARK PANEL, AND IT OVERLAPS THE END OF THE GAMES BAND
 * on purpose — the seam between "what children play" and "what happens to the
 * camera" is the exact question a buyer has at that point in the page.
 */
export function Privacy({ t }: PrivacyProps) {
  return (
    <section
      id={SECTION_IDS.privacy}
      tabIndex={-1}
      className="relative z-10 mx-auto -mt-16 w-full max-w-[1240px] scroll-mt-24 px-5 outline-none sm:px-6 lg:-mt-20 lg:px-10"
    >
      <Reveal>
        <div className="kq-privacy-panel relative grid items-center gap-10 overflow-hidden rounded-2xl px-6 py-12 sm:px-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14 lg:rounded-[3rem] lg:px-16 lg:py-16">
          <div className="relative mx-auto w-full max-w-[420px]">
            <div aria-hidden="true" className="kq-glow absolute inset-[8%] rounded-pill bg-teal/45 blur-3xl" />
            <img
              src={HOME_ART.privacyShield}
              alt={t.privacy.imageAlt}
              width={720}
              height={720}
              loading="lazy"
              decoding="async"
              className="kq-float-sm relative w-full"
            />
          </div>

          <div>
            <p className="flex items-center gap-3 font-dash text-dash-xs font-bold text-kid-camera-line">
              <span aria-hidden="true" className="h-0.5 w-7 rounded-pill bg-teal" />
              {t.privacy.label}
            </p>
            <h2 className="kq-h2 mt-4 font-dash font-extrabold tracking-tight text-surface">{t.privacy.heading}</h2>
            <ul className="mt-8 space-y-3">
              {t.privacy.points.map((point, i) => (
                <li
                  key={point.title}
                  className="flex gap-4 rounded-xl border border-surface/15 bg-surface/8 p-4 sm:p-5"
                >
                  <span
                    aria-hidden="true"
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-teal text-surface shadow-[0_4px_0_color-mix(in_srgb,var(--color-teal)_60%,var(--color-ink))]"
                  >
                    {ICONS[i]}
                  </span>
                  <span>
                    <span className="block font-dash text-dash-md font-extrabold text-surface">{point.title}</span>
                    <span className="mt-0.5 block font-dash text-dash-sm text-surface/80">{point.body}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
